import React, { useEffect, useState } from 'react';

import { Space, Typography, Tag, Flex } from 'antd';

import { ContractState } from '../api/getShipment';
import TruckIcon from '@/components/icons/TruckIcon';
import WarningIconV2 from '@/components/icons/WarningIconV2';
import useRemoteControl from '@/features/boxes/components/RemoteControl/hook/useRemoteControl';

const { Text, Link } = Typography;

interface TelemetrySectionProps {
  geolocalisation?: {
    latitude: number;
    longitude: number;
  };
  boxStatus?: string;
  contractState?: ContractState;
  lpui: string;
}

export const TelemetrySection: React.FC<TelemetrySectionProps> = ({
  geolocalisation,
  boxStatus,
  contractState,
  lpui,
}) => {
  const { getConnectivity } = useRemoteControl(lpui);
  const [connectedStatusUpdatedAt, setConnectedStatusUpdatedAt] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchConnectivity = async () => {
      if (lpui) {
        const data = await getConnectivity();

        if (data?.connected_status_updated_at) {
          setConnectedStatusUpdatedAt(data.connected_status_updated_at);
        }
      }
    };

    fetchConnectivity();
  }, [lpui, getConnectivity]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Text
          style={{
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: '125%',
          }}
        >
          Telemetry datas
        </Text>
        <Tag
          color="blue"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5625rem',
          }}
          data-testid="shipment-contract-state"
        >
          <TruckIcon />{' '}
          {contractState ? ContractState[contractState] : 'Unknown'}
        </Tag>
      </Flex>

      {/* Last connection */}
      {connectedStatusUpdatedAt && (
        <Flex justify="space-between" align="center">
          <Space direction="vertical" size={2}>
            <Space direction="horizontal">
              <WarningIconV2 />
              <Text
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: '125%',
                }}
              >
                Last connection with the BOX
              </Text>
            </Space>
            <Text
              style={{
                fontSize: 'O.875rem',
                fontWeight: 400,
                lineHeight: '135%',
                color: '#595959',
              }}
            >
              {new Date(connectedStatusUpdatedAt)
                .toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: undefined,
                })
                .replace(',', '')}{' '}
              at{' '}
              {new Date(connectedStatusUpdatedAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: false,
              })}
            </Text>
          </Space>
        </Flex>
      )}

      {/* Last know geolocalisation */}
      {geolocalisation && (
        <Space direction="vertical" size={2}>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: '125%',
            }}
          >
            Last know geolocalisation
          </Text>
          <Text
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '135%',
              color: '#595959',
            }}
          >
            {geolocalisation.latitude}, {geolocalisation.longitude}
          </Text>
          <Link
            href={`https://www.google.com/maps/place/${geolocalisation.latitude},${geolocalisation.longitude}`}
            target="_blank"
            style={{
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '135%',
              color: '#595959',
              textDecoration: 'underline',
            }}
          >
            Open in Google Maps
          </Link>
        </Space>
      )}

      {/* Last BOX status */}
      {boxStatus && (
        <Space direction="vertical" size={2}>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: '125%',
            }}
          >
            Last BOX status
          </Text>
          <Text
            style={{
              fontSize: '0.875rem',
              fontWeight: 400,
              lineHeight: '135%',
              color: '#595959',
            }}
          >
            {boxStatus}
          </Text>
        </Space>
      )}
    </Space>
  );
};
